import React, { useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';

interface Props {
  tags: string[];
  // Distinct tags already in use, shown as autocomplete suggestions.
  suggestions?: string[];
  // Persist handlers — should call the API and update the parent's tag state
  // from the server response (the canonical, normalized list).
  onAdd: (tag: string) => Promise<void> | void;
  onRemove: (tag: string) => Promise<void> | void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  label?: string;
  placeholder?: string;
}

// Free-form tag editor: deletable chips + an autocomplete that both suggests
// existing tags and accepts brand-new ones (freeSolo). The server normalizes
// and de-dupes, so we treat its response as the source of truth and just diff
// the Autocomplete value to decide whether a tag was added or removed.
export default function TagEditor({
  tags,
  suggestions = [],
  onAdd,
  onRemove,
  disabled = false,
  size = 'small',
  label = 'Tags',
  placeholder = 'Add tag…',
}: Props) {
  const [busy, setBusy] = useState(false);

  const handleChange = async (_e: unknown, next: string[]) => {
    const cleaned = next.map(t => t.trim()).filter(Boolean);
    const lower = (arr: string[]) => arr.map(t => t.toLowerCase());
    const currentLower = lower(tags);
    const added = cleaned.find(t => !currentLower.includes(t.toLowerCase()));
    const removed = tags.find(t => !lower(cleaned).includes(t.toLowerCase()));

    setBusy(true);
    try {
      if (added) await onAdd(added);
      else if (removed) await onRemove(removed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      size={size}
      options={suggestions}
      value={tags}
      disabled={disabled || busy}
      onChange={handleChange}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {busy ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
